// src/pages/dashboard/TeamView/TeamView.jsx
import "../../styles/teamview.css";
import "../../styles/myskills.css";
import { FaCalendarAlt, FaPaperclip, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import ReactDOM from "react-dom";
import useTeamView from "./useTeamView";
import { API_BASE } from "../../api/common_api";

// ── Reusable Pagination Bar ──────────────────────────────────────
function Pagination({ page, total, onChange }) {
  if (total <= 1) return null;
  return (
    <div className="tv-pagination">
      <button
        className="tv-page-btn"
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
      >
        <FaChevronLeft />
      </button>

      {Array.from({ length: total }, (_, i) => i + 1).map((p) => (
        <button
          key={p}
          className={`tv-page-btn ${p === page ? "active" : ""}`}
          onClick={() => onChange(p)}
        >
          {p}
        </button>
      ))}

      <button
        className="tv-page-btn"
        onClick={() => onChange(page + 1)}
        disabled={page === total}
      >
        <FaChevronRight />
      </button>
    </div>
  );
}

export default function TeamView() {

  const {
    user,
    managers,
    subManagers,
    members,
    selectedManager, setSelectedManager,
    selectedSubManager, setSelectedSubManager,
    selectedMember, setSelectedMember,
    viewType, setViewType,
    period, setPeriod,
    reflections,
    loadingRef,
    expandedIndex, setExpandedIndex,

    refPage, setRefPage,
    refTotalPages,
    pagedReflections,

    timesheet,
    tsLoading,
    tsProject, setTsProject,
    tsPeriod, setTsPeriod,
    tsValue, setTsValue,

    tsPage, setTsPage,
    tsTotalPages,
    pagedTimesheet,

    skills,
    skillsPeriod, setSkillsPeriod,
    skillsDate, setSkillsDate,
    loadingSkills,
    openMissing, setOpenMissing,
    missingType, setMissingType,
    missingPeriod, setMissingPeriod,
    missingDate, setMissingDate,
    missingUsers,
    missingLoading,
    missingSearch, setMissingSearch,
    totalTeamCount,
    submittedCount,
    avatar,
    onManagerSelect,
    onSubManagerSelect,
    loadReflections,
    loadTimesheet,
    loadSkills,
    fetchMissing,
  } = useTeamView();

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="teamview-page">
      {/* HEADER */}
      <div className="team-insights-header">
        <div className="team-insights-title-box">
          <p className="team-insights-subtitle">View team performance.</p>
        </div>
        <button className="missing-btn-header" onClick={() => setOpenMissing(true)}>
          Missing Reports
        </button>
      </div>

      {/* TOP FILTERS */}
      <div className="tv-filter-card">
        <div className="tv-filter-row">
          {user?.RoleID === 1 && (
            <div className="tv-field-sm">
              <label><FaCalendarAlt /> MANAGER</label>
              <select value={selectedManager} onChange={(e) => onManagerSelect(e.target.value)}>
                <option value="">Select Manager</option>
                {managers.map((m) => (
                  <option key={m.EmailID} value={m.EmailID}>{m.Name}</option>
                ))}
              </select>
            </div>
          )}

          {subManagers.length > 0 && (
            <div className="tv-field-sm">
              <label><FaCalendarAlt /> SUB MANAGER</label>
              <select value={selectedSubManager} onChange={(e) => onSubManagerSelect(e.target.value)}>
                <option value="">Select</option>
                {subManagers.map((sm) => (
                  <option key={sm.EmailID} value={sm.EmailID}>{sm.Name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="tv-field-sm">
            <label><FaCalendarAlt /> EMPLOYEE</label>
            <select
              value={selectedMember}
              className="tv-employee-select"
              onChange={(e) => {
                const v = e.target.value;
                setSelectedMember(v);
                // auto-load handled by useEffect in hook
                if (viewType === "reflection") loadReflections(v);
                else if (viewType === "skills") loadSkills(v);
                // timesheet auto-loads via useEffect
              }}
            >
              <option value="">Select Employee</option>
              {members.map((m) => (
                <option key={m.EmailID} value={m.EmailID}>{m.Name}</option>
              ))}
            </select>
          </div>

          <div className="tv-field-sm">
            <label><FaCalendarAlt /> VIEW TYPE</label>
            <select value={viewType} onChange={(e) => setViewType(e.target.value)}>
              <option value="reflection">Reflections</option>
              <option value="timesheet">Timesheet</option>
              <option value="skills">Skills</option>
            </select>
          </div>
        </div>
      </div>

      {/* ══════════════════ SKILLS SECTION ══════════════════ */}
      {viewType === "skills" && (
        <>
          <div className="tv-top-filter" style={{ gap: "12px" }}>
            <label><FaCalendarAlt /> PERIOD</label>
            <select
              className="ms-period-select"
              value={skillsPeriod}
              onChange={(e) => {
                const p = e.target.value;
                setSkillsPeriod(p);
                const now = new Date();
                const year = now.getFullYear();
                if (p === "weekly") {
                  const first = new Date(year, 0, 1);
                  const week = String(Math.ceil(((now - first) / 86400000 + first.getDay() + 1) / 7)).padStart(2, "0");
                  setSkillsDate(`${year}-W${week}`);
                } else if (p === "monthly") {
                  setSkillsDate(`${year}-${String(now.getMonth() + 1).padStart(2, "0")}`);
                } else {
                  setSkillsDate(today);
                }
              }}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly" selected>Monthly</option>
            </select>

            <input
              className="ms-date-input"
              type={skillsPeriod === "weekly" ? "week" : skillsPeriod === "monthly" ? "month" : "date"}
              value={skillsDate}
              onChange={(e) => setSkillsDate(e.target.value)}
            />

            <button
              className="ms-refresh"
              onClick={() => loadSkills(selectedMember)}
              disabled={!selectedMember}
            >
              🔎 Analyze
            </button>
          </div>

          <h2><FaCalendarAlt /> Team Skills</h2>

          <div className="tv-cards">
            {loadingSkills && <p className="tv-empty">Loading skills…</p>}
            {!loadingSkills && skills.length === 0 && <p className="tv-empty">No skills found.</p>}
            {skills.map((s, idx) => (
              <div className="tv-card" key={idx}>
                <div className="tv-card-header">
                  <div className="tv-avatar">{s.SkillName?.charAt(0) || "S"}</div>
                  <div className="tv-card-info">
                    <div className="tv-name">{s.SkillName}</div>
                    <div className="tv-meta">
                      <span>Level: {s.Level}</span>
                      <span>Score: {s.Score}</span>
                      <span><FaCalendarAlt /> {s.UpdatedDate}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ══════════════════ REFLECTION SECTION ══════════════════ */}
      {viewType === "reflection" && (
        <>
          <div className="tv-top-filter">
            <label><FaCalendarAlt /> PERIOD</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <h2>Reflections</h2>

          {/* Record count */}
          {!loadingRef && reflections.length > 0 && (
            <p className="tv-count">
              Showing {((refPage - 1) * 5) + 1}–{Math.min(refPage * 5, reflections.length)} of {reflections.length} records
            </p>
          )}

          <div className="tv-cards">
            {loadingRef && <p className="tv-empty">Loading reflections…</p>}
            {!loadingRef && reflections.length === 0 && <p className="tv-empty">No reflections found.</p>}

            {pagedReflections.map((rec, idx) => (
              <div
                key={idx}
                className="tv-card"
                onClick={() => setExpandedIndex(expandedIndex === idx ? null : idx)}
              >
                <div className="tv-card-header">
                  <div className="tv-avatar">{avatar(rec.Name)}</div>
                  <div className="tv-card-info">
                    <div className="tv-name">{rec.Name}</div>
                    <div className="tv-meta">
                      <span>{rec.PeriodType}</span>
                      <span><FaCalendarAlt /> {rec.SelectedDate?.split("T")[0]}</span>
                    </div>
                  </div>
                  <div className="tv-arrow">
                    {expandedIndex === idx ? "▲" : "▼"}
                  </div>
                </div>

                {expandedIndex === idx && (
                  <div className="tv-card-body">
                    {[1, 2, 3].map((n) => (
                      <div className="tv-q-block" key={n}>
                        <p className="tv-q">
                          {n}. {rec.Answers[`Question${n}`]?.question}
                        </p>
                        <ul>
                          {(rec.Answers[`Question${n}`]?.answers || []).map((ans, i) => (
                            <li key={i}>{ans}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    {rec.Tasks?.length > 0 && (
                      <div className="tv-q-block">
                        <p className="tv-q">Tasks</p>
                        <ul>
                          {rec.Tasks.map((t, i) => (
                            <li key={i}>{t.Task}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Reflection Pagination */}
          <Pagination page={refPage} total={refTotalPages} onChange={(p) => { setRefPage(p); setExpandedIndex(null); }} />
        </>
      )}

      {/* ══════════════════ TIMESHEET SECTION ══════════════════ */}
      {viewType === "timesheet" && (
        <>
          {/* Filter bar — Apply button triggers filtered load */}
          <div className="tv-filter-card">
            <div className="tv-filter-row">
              <div className="tv-field-sm">
                <label><FaCalendarAlt /> PROJECT</label>
                <input
                  type="text"
                  placeholder="Search by project"
                  value={tsProject}
                  onChange={(e) => setTsProject(e.target.value)}
                />
              </div>

              <div className="tv-field-sm">
                <label><FaCalendarAlt /> PERIOD</label>
                <select value={tsPeriod} onChange={(e) => setTsPeriod(e.target.value)}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div className="tv-field-sm">
                <label><FaCalendarAlt /> SELECT</label>
                <input
                  type={tsPeriod === "daily" ? "date" : tsPeriod === "weekly" ? "week" : "month"}
                  value={tsValue}
                  onChange={(e) => setTsValue(e.target.value)}
                />
              </div>

              <button
                style={{
                  padding: "12px 24px",
                  marginTop: "28px",
                  background: "linear-gradient(135deg,#00d4ff,#9d4edd)",
                  border: "none",
                  color: "#fff",
                  fontWeight: "700",
                  borderRadius: "12px",
                  cursor: "pointer",
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                }}
                onClick={() => loadTimesheet(selectedMember)}
                disabled={!selectedMember}
              >
                <FaCalendarAlt /> Apply
              </button>
            </div>
          </div>

          <h2><FaCalendarAlt /> Timesheet History</h2>

          {/* Record count */}
          {!tsLoading && timesheet.length > 0 && (
            <p className="tv-count">
              Showing {((tsPage - 1) * 5) + 1}–{Math.min(tsPage * 5, timesheet.length)} of {timesheet.length} records
            </p>
          )}

          <div className="tv-cards">
            {tsLoading && <p className="tv-empty">Loading timesheet…</p>}
            {!tsLoading && timesheet.length === 0 && (
              <p className="tv-empty">
                {selectedMember ? "No timesheet found." : "Select an employee to view timesheets."}
              </p>
            )}

            {pagedTimesheet.map((rec, idx) => {
              const recDate = rec.SelectedDate
                ? rec.SelectedDate.split("T")[0]
                : "";

              return (
                <div className="tv-card" key={idx}>
                  <div className="tv-card-header">
                    <div className="tv-avatar"><FaCalendarAlt /></div>
                    <div className="tv-card-info">
                      <div className="tv-name">{recDate}</div>
                      <div className="tv-meta">
                        <span>{rec.Name || rec.empID}</span>
                        <span>{rec.tasks?.length || 0} Tasks</span>
                        <span>
                          <FaCalendarAlt /> Total:{" "}
                          {rec.tasks?.reduce((sum, t) => sum + (parseFloat(t.hours) || 0), 0)}h
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="tv-card-body">
                    {rec.tasks?.map((t, i) => (
                      <div className="tv-q-block" key={i}>
                        <p className="tv-q"><FaCalendarAlt /> {t.project}</p>
                        <ul>
                          <li>Client: {t.client}</li>
                          <li>Task: {t.task}</li>
                          <li>Hours: {t.hours}</li>
                        </ul>
                      </div>
                    ))}
                  </div>

                  {/* ATTACHMENTS */}
                  {rec.attachments && rec.attachments.length > 0 && (
                    <div
                      style={{
                        padding: "10px 16px 12px",
                        borderTop: "1px solid rgba(0,0,0,0.07)",
                        display: "flex",
                        gap: "8px",
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: "12px", color: "#888", fontWeight: 600 }}>
                        📎 Attachments:
                      </span>
                      {rec.attachments.map((att, attIdx) => {
                        const label = att.filename
                          ? att.filename.length > 20
                            ? att.filename.slice(0, 20) + "…"
                            : att.filename
                          : `File ${attIdx + 1}`;
                        const url = `${API_BASE}/timesheet/attachment?empID=${rec.empID}&date=${recDate}&index=${attIdx}`;
                        return (
                          <a
                            key={attIdx}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={att.filename || "Attachment"}
                            style={{
                              background: "linear-gradient(135deg,#00d4ff,#9d4edd)",
                              color: "#fff",
                              borderRadius: "8px",
                              padding: "6px 14px",
                              fontSize: "13px",
                              fontWeight: "600",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              textDecoration: "none",
                            }}
                          >
                            <FaPaperclip /> {label}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Timesheet Pagination */}
          <Pagination page={tsPage} total={tsTotalPages} onChange={(p) => setTsPage(p)} />
        </>
      )}

      {/* ══════════════════ MISSING REPORTS MODAL ══════════════════ */}
      {openMissing && ReactDOM.createPortal(
        <div className="missing-modal-overlay" onClick={() => setOpenMissing(false)}>
          <div className="missing-modal" onClick={(e) => e.stopPropagation()}>

            {/* ── Header ── */}
            <div className="mm-header">
              <div className="mm-header-left">
                <div className="mm-header-icon">📋</div>
                <div>
                  <h3 className="mm-title">Missing Reports</h3>
                  <p className="mm-subtitle">Check who hasn't submitted yet</p>
                </div>
              </div>
              <button className="mm-close-btn" onClick={() => setOpenMissing(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* ── Form ── */}
            <div className="mm-body">
              <div className="mm-form-grid">

                {/* Report Type — custom dropdown */}
                <div className="mm-field">
                  <label className="mm-label">Report Type</label>
                  <div className="mm-select-wrap">
                    <select
                      className="mm-select"
                      value={missingType}
                      onChange={(e) => setMissingType(e.target.value)}
                    >
                      <option value="reflection">📝 Reflections</option>
                      <option value="timesheet">🗂 Timesheet</option>
                    </select>
                    <span className="mm-select-arrow">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </span>
                  </div>
                </div>

                {/* Period — custom dropdown */}
                <div className="mm-field">
                  <label className="mm-label">Period</label>
                  <div className="mm-select-wrap">
                    <select
                      className="mm-select"
                      value={missingPeriod}
                      onChange={(e) => {
                        setMissingPeriod(e.target.value);
                        setMissingDate(today);
                      }}
                    >
                      <option value="daily">☀️ Daily</option>
                      <option value="weekly">📅 Weekly</option>
                      <option value="monthly">📊 Monthly</option>
                    </select>
                    <span className="mm-select-arrow">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                    </span>
                  </div>
                </div>

                {/* Date */}
                <div className="mm-field">
                  <label className="mm-label">Select Date</label>
                  <input
                    className="mm-input"
                    type={missingPeriod === "daily" ? "date" : missingPeriod === "weekly" ? "week" : "month"}
                    value={missingDate}
                    onChange={(e) => setMissingDate(e.target.value)}
                  />
                </div>

                {/* Search */}
                <div className="mm-field">
                  <label className="mm-label">Search Employee</label>
                  <div className="mm-search-wrap">
                    <svg className="mm-search-icon" width="15" height="15" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input
                      className="mm-input mm-input-search"
                      type="text"
                      placeholder="Search by name…"
                      value={missingSearch}
                      onChange={(e) => setMissingSearch(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Fetch Button */}
              <button className="mm-fetch-btn" onClick={fetchMissing}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                Fetch Missing
              </button>

              {/* Results */}
              <div className="mm-results">
                {missingLoading && (
                  <div className="mm-loading">
                    <div className="mm-spinner"/>
                    <span>Checking submissions…</span>
                  </div>
                )}

                {!missingLoading && (
                  <>
                    {/* Stats row */}
                    <div className="mm-stats-row">
                      <div className="mm-stat mm-stat-total">
                        <span className="mm-stat-num">{totalTeamCount}</span>
                        <span className="mm-stat-lbl">Total</span>
                      </div>
                      <div className="mm-stat mm-stat-ok">
                        <span className="mm-stat-num">{submittedCount}</span>
                        <span className="mm-stat-lbl">Submitted</span>
                      </div>
                      <div className="mm-stat mm-stat-miss">
                        <span className="mm-stat-num">{missingUsers.length}</span>
                        <span className="mm-stat-lbl">Missing</span>
                      </div>
                    </div>

                    {missingUsers.length === 0 ? (
                      <div className="mm-all-ok">
                        <span className="mm-all-ok-emoji">🎉</span>
                        <p>Everyone submitted!</p>
                      </div>
                    ) : (
                      <div className="mm-user-list">
                        {missingUsers
                          .filter((u) => u.Name.toLowerCase().includes(missingSearch.toLowerCase()))
                          .map((u, i) => (
                            <div key={i} className="mm-user-card">
                              <div className="mm-user-avatar">{u.Name[0].toUpperCase()}</div>
                              <div className="mm-user-info">
                                <div className="mm-user-name">{u.Name}</div>
                                <div className="mm-user-mail">{u.EmailID}</div>
                              </div>
                              <span className="mm-badge-miss">Missing</span>
                            </div>
                          ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}